
import React from "react";
import { Badge } from "@/components/ui/badge";
import { AlertCircle, RefreshCw, Ban, CheckCircle, ChevronDown } from "lucide-react";
import { Requirement } from "@/types/project";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface RequirementStatusBadgeProps {
  status: string;
  requirement?: Requirement;
  onStatusChange?: (requirement: Requirement, status: string) => void;
  clickable?: boolean;
}

export function RequirementStatusBadge({ 
  status, 
  requirement,
  onStatusChange,
  clickable = false
}: RequirementStatusBadgeProps) {
  const getStatusBadge = (statusValue: string) => {
    switch (statusValue) {
      case 'Incomplete':
        return (
          <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">
            <AlertCircle className="h-3 w-3 mr-1" />
            Incomplete
          </Badge>
        );
      case 'In Progress':
        return (
          <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
            <RefreshCw className="h-3 w-3 mr-1" />
            In Progress
          </Badge>
        );
      case 'Not Applicable':
        return (
          <Badge variant="outline" className="bg-yellow-50 text-yellow-700 border-yellow-200">
            <Ban className="h-3 w-3 mr-1" />
            Not Applicable
          </Badge>
        );
      case 'Ready to Submit':
        return (
          <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
            <CheckCircle className="h-3 w-3 mr-1" />
            Ready to Submit
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="bg-gray-50 text-gray-700 border-gray-200">
            <AlertCircle className="h-3 w-3 mr-1" />
            Unknown
          </Badge>
        );
    }
  };

  if (!clickable || !requirement || !onStatusChange) {
    return getStatusBadge(status);
  }

  return (
    <Select
      value={status}
      onValueChange={(value) => onStatusChange(requirement, value)}
    >
      <SelectTrigger className="w-auto h-auto p-0 border-0 bg-transparent hover:bg-muted/50 focus:ring-0 focus:ring-offset-0">
        <SelectValue>
          {getStatusBadge(status)}
        </SelectValue>
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="Incomplete">
          <div className="flex items-center">
            <AlertCircle className="h-3 w-3 mr-2" />
            <span>Incomplete</span>
          </div>
        </SelectItem>
        <SelectItem value="In Progress">
          <div className="flex items-center">
            <RefreshCw className="h-3 w-3 mr-2" />
            <span>In Progress</span>
          </div>
        </SelectItem>
        <SelectItem value="Not Applicable">
          <div className="flex items-center">
            <Ban className="h-3 w-3 mr-2" />
            <span>Not Applicable</span>
          </div>
        </SelectItem>
        <SelectItem value="Ready to Submit">
          <div className="flex items-center">
            <CheckCircle className="h-3 w-3 mr-2" />
            <span>Ready to Submit</span>
          </div>
        </SelectItem>
      </SelectContent>
    </Select>
  );
}
